import { Component, useEffect, useRef, type ErrorInfo, type ReactNode } from 'react';
import i18next from '@/lib/i18n';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { captureClientException } from '@/lib/sentry';
import styles from './LazyLoadBoundary.module.css';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/** Marks a chunk or locale load failure; every other error must reach AppErrorBoundary. */
export class LazyLoadError extends Error {
  constructor(readonly cause: unknown) {
    super(cause instanceof Error ? cause.message : 'Lazy load failed');
    this.name = 'LazyLoadError';
  }
}

function LoadFailed() {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <div className={`page-gutter ${styles.root}`}>
      <h1 className={`text-heading-l ${styles.title}`}>{i18next.t('common:errors.pageLoadFailedTitle')}</h1>
      <Alert variant="destructive" role="alert" className={styles.alert}>
        <p>{i18next.t('common:errors.pageLoadFailed')}</p>
        <Button ref={ref} type="button" variant="outline" onClick={() => window.location.reload()}>
          {i18next.t('common:errors.retry')}
        </Button>
      </Alert>
    </div>
  );
}

/** Route-level boundary for lazy pages. Retry reloads the page (see lazyWithLocales); it never reloads on its own. */
export class LazyLoadBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    if (error instanceof LazyLoadError) captureClientException(error, { componentStack: info.componentStack });
  }

  override render() {
    // Not a load failure: rethrow so the app-wide boundary handles it.
    if (this.state.error && !(this.state.error instanceof LazyLoadError)) throw this.state.error;
    if (this.state.error) return <LoadFailed />;
    return this.props.children;
  }
}
